import React, { memo, useState, useRef, useEffect, Fragment } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { object, bool, string, arrayOf, shape } from 'prop-types'

import Assets3DCategories from 'Pods/LazyLoaded/Assets3DCategories/Assets3DCategories'
import Assets3DPrintCategories from 'Pods/LazyLoaded/Assets3DPrintCategories/Assets3DPrintCategories'
import { selectStatus, useGenerationStatus } from 'Pods/Shared/GenerationStatus'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import { useNavOverlay, NAV_OVERLAY_IDS } from '../../Hooks/useNavOverlay'
import MenuItem from './Components/MenuItem/MenuItem'

// The Sellers Hub dropdown panel is anchored to the Sellers Hub trigger (its id).
const SELLERS_HUB_ID = 'navigation-sellers-hub'
// The Create with AI dropdown panel is anchored to the Create with AI trigger.
const CREATE_AI_ID = 'navigation-create-ai'
// The 3D Models / 3D Printing mega-menu panels are anchored to their triggers.
const MODELS_ID = 'navigation-3d-models'
const PRINT_ID = 'navigation-3d-print'

// The panel content (link sections, i18n strings, Modelry logo asset) is split
// into its own chunk and fetched only when Sellers Hub is first opened.
let SellersHubDropdown
// The Create with AI panel is likewise split into its own chunk, fetched only
// when Create with AI is first opened.
let CreateAiDropdown
// The 3D Models / 3D Printing mega-menu panels (Assets3DCategories /
// Assets3DPrintCategories, shared with LegacyNavigation) are imported statically,
// NOT code-split: they must render during SSR so their category / type / budget
// links ship in the initial HTML and stay crawlable with JS off. Visibility is then
// driven purely by CSS (MenuItem's persistPanel gate + the `.is-open` reveal in
// _unified-navigation.scss); JS only toggles the class, it no longer mounts them.

// Primary menu (Create with AI, 3D Models, 3D Printing, Hire a Freelancer,
// Sellers Hub) for the redesigned navigation. Items are data-driven from the
// backend `main_menu_items`. The Sellers Hub item opens the Sellers Hub
// dropdown; the remaining `has_dropdown` items are triggers only for now.
// `isActive` mirrors the hamburger so the menu can reveal on mobile (inline
// from the md breakpoint up).
function Menu({
  isActive = false,
  mainMenuItems = EMPTY_OBJECT,
  communityLinks = EMPTY_ARRAY,
  createAiLinks = EMPTY_ARRAY,
  createAiGeneration = EMPTY_OBJECT,
  showFreeTrialTag = false,
  locale = 'en',
  currentUserId = null,
  assetCategories = EMPTY_OBJECT,
  printableCategories = EMPTY_OBJECT,
  byType = EMPTY_ARRAY,
  budgetsData = EMPTY_OBJECT,
  actionsConfig = EMPTY_OBJECT,
}) {
  // Live 3D-model generation status (idle | generating | enhancing | succeeded |
  // failed) and the finished-run projection, read from the shared store the /ai
  // studio feeds. Drives the Create with AI trigger icon and the dropdown result row.
  const storeGenerationStatus = useGenerationStatus(selectStatus)
  const storeLastResult = useGenerationStatus((state) => state.lastResult)
  const ownerUserId = useGenerationStatus((state) => state.userId)
  // The store rehydrates from localStorage synchronously on the client, so a returning
  // user mid-generation would hydrate a different trigger icon than the idle server
  // render. Gate on a post-mount flag so the first client render matches SSR, then let
  // the real status refine the icon/result row in place (same 16px glyph, no shift).
  const [hydrated, setHydrated] = useState(false)
  // localStorage is shared across every user of this browser, so only surface the run to
  // the user who owns it - never a later anon visitor or a different logged-in account.
  // Both ids are normalized to strings (see resolveViewerId), and the compare stays
  // fail-closed: an unresolved viewer (null) withholds the run rather than leaking it.
  const ownedByViewer = currentUserId != null && ownerUserId != null
    && String(ownerUserId) === String(currentUserId)
  const generationStatus = hydrated && ownedByViewer ? storeGenerationStatus : undefined
  const lastResult = hydrated && ownedByViewer ? storeLastResult : null

  const [activeDropdown, setActiveDropdown] = useState(null)
  // >= md (960px) is the inline, hover-driven layout; below that the menu is the
  // stacked hamburger panel where the dropdown expands on click. Default to the
  // hover layout so SSR (no matchMedia) matches the fast-page desktop render.
  const [isHoverViewport, setIsHoverViewport] = useState(true)
  const [isSellersHubLoaded, setIsSellersHubLoaded] = useState(false)
  const [isCreateAiLoaded, setIsCreateAiLoaded] = useState(false)
  const menuRef = useRef(null)
  const menuKeys = Object.keys(mainMenuItems)
  const { openId, open, close } = useNavOverlay(NAV_OVERLAY_IDS.MENU)

  function loadSellersHubDropdown() {
    if (!SellersHubDropdown && !isSellersHubLoaded) {
      import(
        /* webpackChunkName: "sellers-hub-dropdown" */
        'Pods/LazyLoaded/SellersHubDropdown/SellersHubDropdown'
      )
        .then((module) => {
          SellersHubDropdown = module.default
          setIsSellersHubLoaded(true)
        })
        .catch((error) => {
          console.error('Failed to load SellersHubDropdown:', error)
        })
    }
  }

  function loadCreateAiDropdown() {
    if (!CreateAiDropdown && !isCreateAiLoaded) {
      import(
        /* webpackChunkName: "create-ai-dropdown" */
        'Pods/LazyLoaded/CreateAiDropdown/CreateAiDropdown'
      )
        .then((module) => {
          CreateAiDropdown = module.default
          setIsCreateAiLoaded(true)
        })
        .catch((error) => {
          console.error('Failed to load CreateAiDropdown:', error)
        })
    }
  }

  // Dispatch the first-open lazy load for whichever trigger opened. Kept in one
  // place so both the click (handleToggle) and hover/focus (handleOpen) paths
  // fetch the same chunk. The 3D Models / 3D Printing panels are statically
  // imported (SSR-rendered), so they need no load step here.
  function loadDropdown(id) {
    if (id === SELLERS_HUB_ID) {
      loadSellersHubDropdown()
    } else if (id === CREATE_AI_ID) {
      loadCreateAiDropdown()
    }
  }

  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined
    }

    // Hover mode only for the inline layout on a device with a real hovering
    // pointer (mouse). Touch / coarse-pointer devices - even wide ones - fall
    // back to click, so the trigger is always openable by tap/keyboard.
    const query = window.matchMedia('(min-width: 960px) and (hover: hover) and (pointer: fine)')

    function update() {
      setIsHoverViewport(query.matches)
      // Crossing the hover/click boundary swaps the layout under any open panel
      // (a hover-opened desktop dropdown would otherwise linger, pre-expanded,
      // into the stacked mobile menu). Close it so each layout starts clean.
      setActiveDropdown(null)
    }

    update()
    query.addEventListener('change', update)

    return () => query.removeEventListener('change', update)
  }, [])

  // Warm the two code-split dropdown chunks (Sellers Hub, Create with AI) during
  // idle time after hydration so the first hover/click paints the panel instantly
  // instead of waiting on a cold dynamic import. The 3D Models / 3D Printing panels
  // are no longer code-split (imported statically for SSR), so they need no warming.
  useEffect(() => {
    function prefetch() {
      loadSellersHubDropdown()
      loadCreateAiDropdown()
    }

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(prefetch, { timeout: 2000 })

      return () => window.cancelIdleCallback(handle)
    }

    const handle = window.setTimeout(prefetch, 300)

    return () => window.clearTimeout(handle)
  }, [])

  useEffect(() => {
    if (!activeDropdown) {
      return undefined
    }

    function handlePointerDown(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveDropdown(null)
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setActiveDropdown(null)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeDropdown])

  // Release the shared overlay slot once the last dropdown closes. Only ever *closes*
  // (guarded, so it no-ops unless the menu still owns the slot) - claiming the slot is
  // done imperatively in the open handlers below. Deriving the open from `isActive`
  // instead would misfire: when another overlay (e.g. a programmatic/keyboard cart
  // open) evicts the hamburger, `isActive` flips false while `activeDropdown` is still
  // set, and this effect would re-open MENU and clobber the overlay that just opened.
  useEffect(() => {
    if (!isActive && !activeDropdown) {
      close(NAV_OVERLAY_IDS.MENU)
    }
  }, [activeDropdown, isActive, close])

  // Collapse the open dropdown when another overlay takes the shared slot. The
  // hamburger is exempt because on mobile it *contains* the menu (an open nested
  // dropdown must survive the hamburger being open).
  useEffect(() => {
    if (openId !== NAV_OVERLAY_IDS.MENU && openId !== NAV_OVERLAY_IDS.HAMBURGER) {
      setActiveDropdown(null)
    }
  }, [openId])

  function handleToggle(id) {
    loadDropdown(id)

    const willOpen = activeDropdown !== id
    setActiveDropdown(willOpen ? id : null)

    // Desktop only: an open dropdown owns the shared slot so it closes any popover /
    // user menu. On mobile (`isActive`) the dropdown is nested inside the hamburger
    // and must not touch the slot. Closing is left to the release effect above.
    if (!isActive && willOpen) {
      open(NAV_OVERLAY_IDS.MENU)
    }
  }

  function handleOpen(id) {
    loadDropdown(id)

    setActiveDropdown(id)

    if (!isActive) {
      open(NAV_OVERLAY_IDS.MENU)
    }
  }

  function handleClose(closingId) {
    // Only honor a close for the panel that is still active. A hover-out arms a
    // delayed close (MenuItem), so moving straight to a sibling trigger opens the
    // sibling before the old timer fires - guarding on the id keeps the sibling open.
    setActiveDropdown((current) => (current === closingId ? null : current))
  }

  if (menuKeys.length === 0) {
    return null
  }

  return (
    <ul
      ref={menuRef}
      className={classnames('unified-nav__menu', isActive && 'unified-nav__menu--open')}
    >
      {menuKeys.map((key) => {
        const menuItem = mainMenuItems[key]
        const isSellersHub = menuItem.id === SELLERS_HUB_ID
        const isCreateAi = menuItem.id === CREATE_AI_ID
        const isModels = menuItem.id === MODELS_ID
        const isPrint = menuItem.id === PRINT_ID
        // Content is null until the chunk resolves; `hasPanel` keeps the hover /
        // focus wiring active so the first interaction can trigger the load.
        let dropdown = null

        if (isSellersHub && isSellersHubLoaded && SellersHubDropdown) {
          dropdown = <SellersHubDropdown communityLinks={communityLinks} locale={locale} />
        } else if (isCreateAi && isCreateAiLoaded && CreateAiDropdown) {
          dropdown = (
            <CreateAiDropdown
              links={createAiLinks}
              showFreeTrialTag={showFreeTrialTag}
              lastResult={lastResult}
              labels={createAiGeneration}
            />
          )
        } else if (isModels) {
          dropdown = (
            <Assets3DCategories
              assetCategories={assetCategories?.itemCategories || EMPTY_ARRAY}
              byType={byType}
              locale={locale}
              budgets={budgetsData.assetsBudgets}
              baseUrl={budgetsData.assetsBaseUrl}
              actionsConfig={actionsConfig}
            />
          )
        } else if (isPrint) {
          dropdown = (
            <Assets3DPrintCategories
              categories={printableCategories?.itemCategories || EMPTY_ARRAY}
              locale={locale}
              budgets={budgetsData.printableAssetsBudgets}
              baseUrl={budgetsData.printableBaseUrl}
              actionsConfig={actionsConfig}
            />
          )
        }

        return (
          <Fragment key={menuItem.id}>
            {/* `divider` brackets the Sellers Hub item with a leading rule. */}
            {menuItem.divider && <li className="unified-nav__menu-divider" aria-hidden="true" />}
            <MenuItem
              menuItem={menuItem}
              isOpen={activeDropdown === menuItem.id}
              hoverEnabled={isHoverViewport}
              onToggle={handleToggle}
              onOpen={handleOpen}
              onClose={handleClose}
              hasPanel={isSellersHub || isCreateAi || isModels || isPrint}
              persistPanel={isModels || isPrint}
              dropdown={dropdown}
              generationStatus={isCreateAi ? generationStatus : undefined}
            />
          </Fragment>
        )
      })}
      {/* Trailing rule separates the menu from the user actions (inline layout only). */}
      <li className="unified-nav__menu-divider" aria-hidden="true" />
    </ul>
  )
}

Menu.propTypes = {
  isActive: bool,
  mainMenuItems: object,
  communityLinks: arrayOf(shape({
    href: string,
    trackText: string,
    title: string,
    icon: string,
  })),
  createAiLinks: arrayOf(shape({
    id: string,
    href: string,
    title: string,
    trackText: string,
    icon: string,
    tag: string,
  })),
  createAiGeneration: shape({
    readyTitle: string,
    openModel: string,
    errorTitle: string,
    errorHref: string,
    dismiss: string,
  }),
  showFreeTrialTag: bool,
  locale: string,
  currentUserId: string,
  assetCategories: object,
  printableCategories: object,
  byType: arrayOf(shape({
    icon: string,
    name: string,
    path: string,
  })),
  budgetsData: shape({
    assetsBaseUrl: string,
    printableBaseUrl: string,
    assetsBudgets: arrayOf(shape({
      title: string,
      path: string,
      icon: string,
      withSub: bool,
    })),
    printableAssetsBudgets: arrayOf(shape({
      title: string,
      path: string,
      icon: string,
      withSub: bool,
    })),
  }),
  actionsConfig: shape({
    subscriptionMenuEnabled: bool,
    subscriptionFlowEnabled: bool,
    isActiveSubscriber: bool,
  }),
}

export default memo(Menu)
